import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import { useAnalytics } from '../utils/analytics/useAnalytics';

/**
 * Menu configuration for header navigation and dropdowns
 * Each item can be a link or a dropdown with sub-items
 */
const menuConfig = [
  {
    label: 'Home',
    to: '/',
    type: 'link'
  },
  {
    label: 'Products',
    type: 'dropdown',
    key: 'products',
    items: [
      { label: 'Accessibility', to: '/accessibility', type: 'link' },
      { label: 'Portfolios', to: '/portfolios', type: 'link' },
      { label: 'Automated Testing', href: 'https://www.sourcetrooper.com', external: true, type: 'link' }
    ]
  },
  {
    label: 'Research',
    type: 'dropdown',
    key: 'research',
    items: [
      { label: 'Sentiment Analysis', to: '/sentiment-analysis', type: 'link' },
      { label: 'Chat Support', to: '/chat-support', type: 'link' }
    ]
  },
  {
    label: 'Contact Us',
    to: '/contact-us',
    type: 'link'
  }
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const { trackNavigation } = useAnalytics();

  // Helper: check if any sub-item is active
  const isDropdownActive = (items) => {
    return items.some(sub => sub.to && location.pathname === sub.to);
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(open => !open);
  };

  const closeDropdowns = () => {
    setOpenDropdown(null);
  };

  const handleMenuLinkClick = (cb) => {
    setIsMenuOpen(false);
    closeDropdowns();
    if (cb) cb();
  };

  const handleNavigation = (to) => {
    trackNavigation(location.pathname, to);
  };

  // Only open dropdown on click for mobile
  const handleDropdownToggle = key => {
    if (window.innerWidth <= 768) {
    setOpenDropdown(current => current === key ? null : key);
    }
  };

  return (
    <header className="header">
      <nav className={isMenuOpen ? 'open' : ''}>
        <ul>
          {menuConfig.map(item => {
            if (item.type === 'link') {
              if (item.external) {
                return (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleMenuLinkClick(() => trackNavigation(location.pathname, item.href))}
                    >{item.label}</a>
                  </li>
                );
              }
              return (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    onClick={() => handleMenuLinkClick(() => handleNavigation(item.to))}
                    className={location.pathname === item.to ? 'active' : ''}
                  >{item.label}</Link>
                </li>
              );
            }
            if (item.type === 'dropdown') {
              const isOpen = openDropdown === item.key && isMenuOpen && window.innerWidth <= 768;
              const isActive = isDropdownActive(item.items);
              return (
                <li
                  className={`dropdown${isOpen ? ' open' : ''}`}
                  key={item.label}
                >
                  <button
                    className={`dropdown-toggle${isActive ? ' active' : ''}`}
                    onClick={e => {
                      e.preventDefault();
                      handleDropdownToggle(item.key);
                    }}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                  >
                    {item.label} <span className={`dropdown-arrow${isOpen ? ' open' : ''}`}>▼</span>
                  </button>
                    <ul className="dropdown-menu">
                      {item.items.map((sub, idx) => {
                        if (sub.placeholder) {
                          return (
                            <li key={sub.label}>
                              <span className="dropdown-placeholder">{sub.label}</span>
                            </li>
                          );
                        }
                        if (sub.external) {
                          return (
                            <li key={sub.label}>
                              <a
                                href={sub.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleMenuLinkClick(() => trackNavigation(location.pathname, sub.href))}
                              >{sub.label}</a>
                            </li>
                          );
                        }
                        return (
                          <li key={sub.label}>
                            <Link
                              to={sub.to}
                              onClick={() => handleMenuLinkClick(() => handleNavigation(sub.to))}
                              className={location.pathname === sub.to ? 'active' : ''}
                            >{sub.label}</Link>
                          </li>
                        );
                      })}
                    </ul>
                </li>
              );
            }
            return null;
          })}
        </ul>
      </nav>
      <div className={`hamburger${isMenuOpen ? ' open' : ''}`} onClick={() => { toggleMenu(); closeDropdowns(); }}>
        <div className="line"></div>
        <div className="line"></div>
        <div className="line"></div>
      </div>
    </header>
  );
};

export default Header;
